/**
 * DriverMapView — Leaflet map showing real-time driver positions.
 *
 * Props:
 *   locations  — array of DriverLocationDto from the API
 *   loading    — show a loading overlay while fetching
 *   updatedAt  — ISO string of the last successful fetch (for stale indicator)
 *   centre     — optional map centre; if omitted, uses the mean of driver
 *                positions, falling back to London
 *   history    — optional route to render as a polyline instead of live markers
 *
 * Uses react-leaflet MapContainer + TileLayer + Marker + Popup. In history mode
 * it also renders a Polyline plus start/end CircleMarkers.
 * The Leaflet icon fix must have been imported before this component renders
 * (handled globally in main.tsx via lib/leafletIconFix).
 */

import {
  MapContainer,
  TileLayer,
  Marker,
  Popup,
  Polyline,
  CircleMarker,
  Circle,
} from 'react-leaflet';
import L from 'leaflet';
import { MapPin } from 'lucide-react';
import type { MapLocationEntry } from '../../../api/driverLocations';
import styles from './DriverMapView.module.css';

export interface DriverMapViewProps {
  locations: MapLocationEntry[];
  loading?: boolean;
  updatedAt?: string | null;
  /** If omitted, uses the mean of driver positions, falling back to London. */
  centre?: [number, number];
  /** When provided with points, renders a route polyline instead of live markers. */
  history?: {
    driverName: string;
    points: Array<{ latitude: number; longitude: number; recorded_at: string }>;
  };
  /** Optional radius circle overlay (miles), used by the GeoLink Nearby
   *  Bookings page to visualise the search radius around a postcode. */
  radiusOverlay?: { centre: [number, number]; miles: number };
}

const DEFAULT_CENTER: [number, number] = [51.5074, -0.1278]; // London
const DEFAULT_ZOOM = 10;

function formatTime(iso: string): string {
  return new Date(iso).toLocaleTimeString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function formatSpeed(mps: number | null): string {
  if (mps === null) return '—';
  return `${(mps * 3.6).toFixed(1)} km/h`;
}

function isStale(iso: string | null | undefined): boolean {
  if (!iso) return false;
  return Date.now() - new Date(iso).getTime() > 60_000;
}

export function DriverMapView({
  locations,
  loading = false,
  updatedAt,
  centre: centreProp,
  history,
  radiusOverlay,
}: DriverMapViewProps) {
  const historyPoints = history?.points ?? [];
  const hasHistory = historyPoints.length > 0;

  if (!loading && locations.length === 0 && !hasHistory && !radiusOverlay) {
    return (
      <div className={styles.empty}>
        <MapPin size={18} aria-hidden />
        No driver locations available. Drivers must enable location sharing in their app.
      </div>
    );
  }

  // In history mode, centre on the midpoint of the route. Otherwise prefer
  // the radius overlay's centre (when present), then the provided centre
  // prop, then the mean of driver positions, then London.
  const centre: [number, number] = hasHistory
    ? [
        historyPoints[Math.floor(historyPoints.length / 2)]!.latitude,
        historyPoints[Math.floor(historyPoints.length / 2)]!.longitude,
      ]
    : (radiusOverlay?.centre ??
      centreProp ??
      (locations.length > 0
        ? [
            locations.reduce((s, l) => s + l.latitude, 0) / locations.length,
            locations.reduce((s, l) => s + l.longitude, 0) / locations.length,
          ]
        : DEFAULT_CENTER));

  return (
    <div className={styles.wrapper}>
      <MapContainer
        center={centre}
        zoom={DEFAULT_ZOOM}
        className={styles.map}
        style={{ height: '520px', width: '100%' }}
        scrollWheelZoom
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {radiusOverlay && (
          <Circle
            center={radiusOverlay.centre}
            radius={radiusOverlay.miles * 1609.34}
            pathOptions={{ color: '#3b82f6', fillOpacity: 0.05, weight: 1.5 }}
          />
        )}
        {locations.map((loc) => {
          const name = loc.type === 'driver' ? loc.driver_name : loc.staff_name;
          const icon = L.divIcon({
            className: '',
            html: `<div class="${loc.type === 'driver' ? styles.driverPin : styles.staffPin}"></div>`,
            iconSize: [24, 24],
            iconAnchor: [12, 24],
          });
          return (
            <Marker key={`${loc.type}-${loc.enc_id}`} position={[loc.latitude, loc.longitude]} icon={icon}>
              <Popup>
                <p className={styles.popupName}>{name}</p>
                <p className={styles.popupMeta}>
                  Last seen: {formatTime(loc.recorded_at)}
                  <br />
                  Speed: {formatSpeed(loc.speed_mps)}
                  {loc.accuracy_m !== null && (
                    <>
                      <br />
                      Accuracy: ±{Math.round(loc.accuracy_m)}m
                    </>
                  )}
                </p>
              </Popup>
            </Marker>
          );
        })}

        {hasHistory && (
          <>
            <Polyline
              positions={historyPoints.map((p) => [p.latitude, p.longitude])}
              pathOptions={{ color: '#f97316', weight: 3, opacity: 0.8 }}
            />
            {/* Start marker — green */}
            <CircleMarker
              center={[historyPoints[0]!.latitude, historyPoints[0]!.longitude]}
              radius={8}
              pathOptions={{ color: '#10b981', fillColor: '#10b981', fillOpacity: 1 }}
            >
              <Popup>
                <p className={styles.popupName}>{history!.driverName}</p>
                <p className={styles.popupMeta}>
                  Start: {formatTime(historyPoints[0]!.recorded_at)}
                </p>
              </Popup>
            </CircleMarker>
            {/* End marker — red */}
            <CircleMarker
              center={[
                historyPoints[historyPoints.length - 1]!.latitude,
                historyPoints[historyPoints.length - 1]!.longitude,
              ]}
              radius={8}
              pathOptions={{ color: '#ef4444', fillColor: '#ef4444', fillOpacity: 1 }}
            >
              <Popup>
                <p className={styles.popupName}>{history!.driverName}</p>
                <p className={styles.popupMeta}>
                  End: {formatTime(historyPoints[historyPoints.length - 1]!.recorded_at)}
                </p>
              </Popup>
            </CircleMarker>
          </>
        )}
      </MapContainer>

      <div className={styles.overlay}>
        {hasHistory ? (
          <span className={styles.badge}>
            {historyPoints.length} point{historyPoints.length !== 1 ? 's' : ''} · {history!.driverName}
          </span>
        ) : (
          <span className={styles.badge}>
            {locations.length} online
          </span>
        )}
        {!hasHistory && isStale(updatedAt) && (
          <span className={`${styles.badge} ${styles.staleWarning}`}>
            Data may be stale
          </span>
        )}
      </div>
    </div>
  );
}
